import FeedbackAttachment from '@/routes/FeedbackAttachment'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import DataCatalog from '@/routes/DataCatalog'
import MatchEntity from '@/routes/DataCatalog/MatchEntity'
import Login from '@/routes/Login'
import GraphEditor from '@/routes/GraphEditor'
import Onboarding from '@/routes/Onboarding'
import BasicDetails from '@/routes/Onboarding/BasicDetails'
import DynamicQuestions from '@/routes/Onboarding/DynamicQuestions'
import AccountDetails from '@/routes/AccountDetails'
import IntelligentBoard from '@/routes/IntelligentBoard'
import BoardCanvas from '@/routes/IntelligentBoard/BoardCanvas'
import KnowledgeGraph from '@/routes/KnowledgeGraph'
import PlcParser from '@/routes/PlcParser'
import TagMapping from '@/routes/TagMapping'
import TagMappingDetail from '@/routes/TagMapping/TagDetail'
import HumanExpertKnowledge from '@/routes/HumanExpertKnowledge'
import HumanExpertTopicDetails from '@/routes/HumanExpertKnowledge/TopicDetails'
import Workflow from '@/routes/Workflow'
import WorkflowBuilder from '@/routes/Workflow/builder/WorkflowBuilder'
import LoginProtectedRoute from '@/components/LoginProtectedRoute'
import AnnotatorRestrictedRoute from '@/components/AnnotatorRestrictedRoute'
import PdfViewer from '@/routes/PdfViewer'
import Datasheet from '@/routes/Datasheet'
import Timeseries from '@/routes/Timeseries'
import LiveStream from '@/routes/LiveStream'
import VideoBrief from '@/routes/VideoBrief'
import SettingsPage from '@/routes/SettingsPage'
import InterviewViewer from '@/routes/InterviewViewer'
import UserArtifactViewer from '@/routes/UserArtifactViewer'
import Connectors from '@/routes/ConnectorCatalog'
import Jobs from '@/routes/JobCatalog'
import AIChat from '@/routes/AIChat'
import MeetingBot from '@/routes/MeetingBot'
import DigitalCoworker from '@/routes/DigitalCoworker'
import NewDigitalCoworker from '@/routes/DigitalCoworker/NewDigitalCoworker'
import ConnectAccounts from '@/routes/DigitalCoworker/ConnectAccounts'
import SetupInterviews from '@/routes/DigitalCoworker/SetupInterviews'
import WizardSuccessPage from '@/routes/DigitalCoworker/WizardSuccessPage'
import ChatPageDC from '@/routes/DigitalCoworker/ChatPage'
import ZoomCallback from '@/components/ZoomCallback'
import GoogleMeetCallback from '@/components/GoogleMeetCallback'
import MSTeamsCallback from '@/components/MSTeamsCallback'
import DigitalCoworkerGoogleCallback from '@/components/DigitalCoworkerGoogleCallback'
import PidSetViewer from '@/routes/PidSetViewer'
import DigitalCoworkerOneDriveCallback from '@/components/DigitalCoworkerOneDriveCallback'
import GraphEmbedView from '@/routes/GraphEmbedView'
import TimeseriesEmbedView from '@/routes/TimeseriesEmbedView'
import RevampComponents from '@/routes/RevampComponents'
import KgTracePage from '@/features/kg-trace/KgTracePage'
import KgDebugPage from '@/features/kg-debug/KgDebugPage'
// PR 14 (gap closure): visual-regression test-mount route. Imported
// statically — Vite's import.meta.glob inside RenderFixture handles
// the fixture eager-load at build time. The route entry below is
// guarded by import.meta.env.DEV so production builds don't expose
// the path.
import RenderFixture from '@/routes/__test__/RenderFixture'
import { enableFabricEmbedMode, isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { NotFoundPage } from '@/routes/ErrorPage'
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary'
import { RootLayout } from './RootLayout'


// Synchronously enable embed mode for /embed/* routes before React renders.
// This prevents a race condition where loadBoard() could run before
// enableFabricEmbedMode() if both were in useEffect hooks.
if (window.location.pathname.startsWith('/embed/') && !isFabricEmbedMode()) {
  enableFabricEmbedMode()
}

// PR 14 (gap closure): test-only routes for visual regression. Only
// registered in dev/preview builds — production tree-shakes these.
const TEST_ROUTES = import.meta.env.DEV
  ? [
      {
        path: '/__test__/render',
        element: <RenderFixture />,
      },
    ]
  : []

export const router = createBrowserRouter([
  ...TEST_ROUTES,
  {
    errorElement: <RouteErrorBoundary />,
    element: <RootLayout />,
    children: [
  {
    path: '/feedback/attachments/:id',
    element: <LoginProtectedRoute><FeedbackAttachment /></LoginProtectedRoute>,
  },
  {
    path: '/',
    element: <Login />,
  },
  {
    // AGNT-685 forensics page — engineer-facing debug tool that
    // replays the exact tool calls + KG files an agent touched for
    // a given query. Standalone (no PageLayout / Navigation) — the
    // expected entry point is "open in new tab from chat 🔍 link
    // or eval report row," not the app's main nav.
    path: '/kg-trace/:correlationId',
    element: (
      <LoginProtectedRoute>
        <KgTracePage />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/kg-debug',
    element: (
      <LoginProtectedRoute>
        <KgDebugPage />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/intelligent-board',
    element: (
      <LoginProtectedRoute>
        <IntelligentBoard />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker/:id/chat',
    element: (
      <LoginProtectedRoute>
        <ChatPageDC />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker',
    element: (
      <LoginProtectedRoute>
        <DigitalCoworker />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker/new',
    element: (
      <LoginProtectedRoute>
        <NewDigitalCoworker />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker/connect-accounts',
    element: (
      <LoginProtectedRoute>
        <ConnectAccounts />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker/setup-interviews',
    element: (
      <LoginProtectedRoute>
        <SetupInterviews />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/digital-coworker/success',
    element: (
      <LoginProtectedRoute>
        <WizardSuccessPage />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/data-catalog',
    element: (
      <LoginProtectedRoute>
        <DataCatalog />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/data-catalog/match-entity',
    element: (
      <LoginProtectedRoute>
        <MatchEntity />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/tag-mapping',
    element: (
      <LoginProtectedRoute>
        <TagMapping />
      </LoginProtectedRoute>
    ),
  },
  {
    // Catalog PLC document viewer: loads the stored parse artifact (VISN-305).
    path: '/plc-parser/:docId',
    element: (
      <LoginProtectedRoute>
        <PlcParser />
      </LoginProtectedRoute>
    ),
  },
  {
    // Flag-gated inside the page (plc_program_ingestion_enabled) — VISN-305.
    path: '/plc-parser',
    element: (
      <LoginProtectedRoute>
        <PlcParser />
      </LoginProtectedRoute>
    ),
  },
  {
    // The tag is URL-encoded — parse content can carry slashes and spaces.
    path: '/tag-mapping/:tag',
    element: (
      <LoginProtectedRoute>
        <TagMappingDetail />
      </LoginProtectedRoute>
    ),
  },
  {
    // DSGN-63: the Knowledge Graph moved under the Data area so its URL
    // matches where the sidebar puts it. Old bookmarks and links keep working.
    path: '/data/graph',
    element: (
      <LoginProtectedRoute>
        <KnowledgeGraph />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/knowledge-graph',
    element: <Navigate to="/data/graph" replace />,
  },
  {
    path: '/human-expert-knowledge',
    element: (
      <LoginProtectedRoute>
        <HumanExpertKnowledge />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/human-expert-knowledge/:id',
    element: (
      <LoginProtectedRoute>
        <HumanExpertTopicDetails />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/workflow',
    element: (
      <LoginProtectedRoute>
        <Workflow />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/workflow/new',
    element: (
      <LoginProtectedRoute>
        <WorkflowBuilder />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/pnid-set/:set_id',
    element: (
      <LoginProtectedRoute>
        <PidSetViewer />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <LoginProtectedRoute>
        <Onboarding />
      </LoginProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <BasicDetails />,
      },
      {
        path: 'questions',
        element: <DynamicQuestions />,
      },
    ],
  },
  {
    path: '/account-details',
    element: (
      <LoginProtectedRoute>
        <AccountDetails />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/graph-editor/:graph_id',
    element: (
      <LoginProtectedRoute>
        <GraphEditor />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/board/:board_id',
    element: (
      <LoginProtectedRoute>
        <BoardCanvas />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/document/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <PdfViewer />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    // Embed-friendly route: auth handled via PostMessage, not Keycloak
    path: '/embed/document/:doc_id',
    element: <PdfViewer />,
  },
  {
    // Embed-friendly route for P&ID graph viewer: auth handled via PostMessage
    path: '/embed/graph/:graph_id',
    element: <GraphEmbedView />,
  },
  {
    // Embed-friendly route for timeseries datasheet viewer: auth handled via PostMessage
    path: '/embed/timeseries/:doc_id',
    element: <TimeseriesEmbedView />,
  },
  {
    // Embed-friendly route for Intelligent Board: auth handled via PostMessage
    path: '/embed/board/:board_id',
    element: <BoardCanvas />,
  },
  {
    path: '/datasheet/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <Datasheet />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/timeseries/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <Timeseries />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    // Live Eventhouse / streamed-tag anchors. Separate from /timeseries because
    // a live stream has no file and no copied values, so the file-shaped
    // viewers don't apply — and the timeseries viewer stays untouched.
    path: '/live-stream/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <LiveStream />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    // A rendered "Explain as video" brief: in-product player plus the slide
    // list with each slide's source file and page.
    path: '/video/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <VideoBrief />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/interview/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <InterviewViewer />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    // FS-557 "User Artifacts" — call-transcript viewer/editor.
    path: '/user-artifact/:doc_id',
    element: (
      <LoginProtectedRoute>
        <AnnotatorRestrictedRoute>
          <UserArtifactViewer />
        </AnnotatorRestrictedRoute>
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/revamp-components',
    element: <RevampComponents />,
  },
  // TODO: Disabling route partially - Remove all it's dependencies
  // {
  //   name: 'search',
  //   path: '/search',
  // },
  // {
  //   path: '/search',
  //   element: (
  //     <LoginProtectedRoute>
  //       <Search />
  //     </LoginProtectedRoute>
  //   ),
  // },
  {
    path: '/connectors',
    element: (
      <LoginProtectedRoute>
        <Connectors />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/jobs',
    element: (
      <LoginProtectedRoute>
        <Jobs />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/chat/:chat_id?',
    element: (
      <LoginProtectedRoute>
        <AIChat />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <LoginProtectedRoute>
        <SettingsPage />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/meeting',
    element: (
      <MeetingBot />
    ),
  },
  {
    path: '/auth/callback/zoom',
    element: <ZoomCallback />,
  },
  {
    path: '/auth/callback/google-meet',
    element: <GoogleMeetCallback />,
  },
  {
    path: '/auth/callback/ms-teams',
    element: <MSTeamsCallback />,
  },
  {
    path: '/auth/callback/digital-coworker-google',
    element: (
      <LoginProtectedRoute>
        <DigitalCoworkerGoogleCallback />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '/auth/callback/digital-coworker-onedrive',
    element: (
      <LoginProtectedRoute>
        <DigitalCoworkerOneDriveCallback />
      </LoginProtectedRoute>
    ),
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
    ],
  },
])
